import { FC, useState, useEffect, useCallback } from "react";
import styles from "./MediaGallery.module.css";
import { Photo, Video } from "@types";
import { MediaModal } from "./MediaModal";
import { ThumbnailGenerator } from "@utils/ThumbnailGenerator";
// import debugService from "@api/debugService";

interface MediaItem {
  type: "photo" | "video";
  photo?: Photo;
  video?: Video;
  id: string;
  hasError?: boolean;
  isLoading?: boolean;
}

interface MediaGalleryProps {
  photos?: Photo[];
  videos?: Video[];
  initialIndex?: number;
  onMediaChange?: (index: number) => void;
}

export const MediaGallery: FC<MediaGalleryProps> = ({
  photos = [],
  videos = [],
  initialIndex = -1,
  onMediaChange,
}) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(initialIndex);
  const [isModalOpen, setIsModalOpen] = useState(initialIndex >= 0);

  // Subscribe to thumbnail updates
  useEffect(() => {
    const unsubscribe = ThumbnailGenerator.subscribeThumbnailUpdates(
      (videoUrl) => {
        // Проверяем, есть ли у нас видео с таким URL
        const mediaItemsToUpdate = [...mediaItems];
        let hasChanges = false;

        for (let i = 0; i < mediaItemsToUpdate.length; i++) {
          const item = mediaItemsToUpdate[i];
          if (item.type === "video" && item.video?.url === videoUrl) {
            hasChanges = true;
            break;
          }
        }

        // Если обнаружены изменения, перерисовываем компонент
        if (hasChanges) {
          setMediaItems([...mediaItemsToUpdate]);
        }
      }
    );

    // Отписка при размонтировании
    return () => {
      // debugService.info("MediaGallery: отписка от обновлений миниатюр");
      unsubscribe();
    };
  }, [mediaItems]);
  // Функция для предварительной загрузки и проверки изображения
  const preloadImage = useCallback((url: string, itemId: string) => {
    const img = new Image();

    img.onload = () => {
      setMediaItems((prevItems) =>
        prevItems.map((item) =>
          item.id === itemId ? { ...item, isLoading: false } : item
        )
      );
    };

    img.onerror = () => {
      setMediaItems((prevItems) =>
        prevItems.map((item) =>
          item.id === itemId
            ? { ...item, hasError: true, isLoading: false }
            : item
        )
      );
    };

    img.src = url;
  }, []);

  // Генерация уникальных ID для медиа при инициализации
  useEffect(() => {
    const photoItems: MediaItem[] = Array.isArray(photos)
      ? photos
          .filter((photo) => photo && photo.url)
          .map((photo, index) => ({
            type: "photo",
            photo,
            id: `photo-${index}-${Date.now()}`,
            hasError: false,
            isLoading: true,
          }))
      : [];

    const videoItems: MediaItem[] = Array.isArray(videos)
      ? videos
          .filter((video) => video && video.url)
          .map((video, index) => ({
            type: "video",
            video,
            id: `video-${index}-${Date.now()}`,
            hasError: false,
            isLoading: false, // Видео не требуют предварительной загрузки
          }))
      : [];

    const allItems = [...photoItems, ...videoItems];
    setMediaItems(allItems);

    // Предварительная проверка доступности фотографий
    photoItems.forEach((item) => {
      if (item.photo?.url) {
        preloadImage(item.photo.url, item.id);
      }
    });

    // Если указан initialIndex, проверяем его валидность
    if (initialIndex >= 0 && initialIndex < allItems.length) {
      setSelectedIndex(initialIndex);
      setIsModalOpen(true);
    }
  }, [photos, videos, initialIndex, preloadImage]);

  // Функции навигации
  const goToNextMedia = useCallback(() => {
    if (
      selectedIndex <
      mediaItems.filter(
        (item) => (!item.hasError || item.type === "video") && !item.isLoading
      ).length -
        1
    ) {
      const nextIndex = selectedIndex + 1;
      setSelectedIndex(nextIndex);
      onMediaChange?.(nextIndex);
    }
  }, [selectedIndex, mediaItems, onMediaChange]);

  const goToPrevMedia = useCallback(() => {
    if (selectedIndex > 0) {
      const prevIndex = selectedIndex - 1;
      setSelectedIndex(prevIndex);
      onMediaChange?.(prevIndex);
    }
  }, [selectedIndex, onMediaChange]);

  const handleItemClick = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      setIsModalOpen(true);
      onMediaChange?.(index);
    },
    [onMediaChange]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Обработчик ошибок загрузки медиа
  const handleMediaError = useCallback(
    (itemId: string, isVideo: boolean = false) => {
      setMediaItems((prevItems) =>
        prevItems.map((item) =>
          item.id === itemId
            ? {
                ...item,
                hasError: isVideo ? false : true, // Не отмечаем видео как ошибочное
                isLoading: false,
              }
            : item
        )
      );
    },
    []
  );

  // Обработчик успешной загрузки медиа
  const handleMediaLoad = useCallback((itemId: string) => {
    setMediaItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, isLoading: false } : item
      )
    );
  }, []);

  // Управление с клавиатуры
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;

      if (e.key === "ArrowRight") {
        goToNextMedia();
      } else if (e.key === "ArrowLeft") {
        goToPrevMedia();
      } else if (e.key === "Escape") {
        handleCloseModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen, goToNextMedia, goToPrevMedia, handleCloseModal]);

  // Рендер компонента ленты миниатюр
  const renderThumbnails = () => {
    // Фильтруем элементы с ошибками загрузки и те, которые еще загружаются
    // При этом оставляем видео с ошибками миниатюр
    const filteredItems = mediaItems.filter(
      (item) => (!item.hasError || item.type === "video") && !item.isLoading
    );

    if (filteredItems.length === 0) {
      return null;
    }

    return (
      <div className={styles.thumbnailsContainer}>
        {filteredItems.map((item, index) => {
          // Определяем, является ли видео вертикальным
          const isVertical =
            item.type === "video" &&
            ThumbnailGenerator.isVerticalVideo(item.video!);

          return (
            <div
              key={item.id}
              className={`${styles.thumbnailWrapper} ${
                index === selectedIndex && isModalOpen
                  ? styles.thumbnailActive
                  : ""
              }`}
              onClick={() => handleItemClick(index)}
            >
              {item.type === "photo" ? (
                <img
                  src={item.photo?.url}
                  alt={`Фото ${index + 1}`}
                  className={styles.thumbnailImage}
                  loading="lazy"
                  onLoad={() => handleMediaLoad(item.id)}
                  onError={(e) => {
                    console.log(e);
                    // debugService.warn("Ошибка загрузки миниатюры фото", {
                    //   photoId: item.id,
                    // });
                    handleMediaError(item.id);
                  }}
                />
              ) : (
                // Используем класс videoThumbnail только для миниатюр
                <div className={styles.videoThumbnail}>
                  <img
                    src={ThumbnailGenerator.getVideoThumbnailUrl(item.video!)}
                    alt={`Видео ${index + 1}`}
                    className={styles.thumbnailImage}
                    loading="lazy"
                    style={{
                      objectFit: isVertical ? "contain" : "cover",
                      backgroundColor: isVertical ? "#000" : "transparent",
                    }}
                    onLoad={() => handleMediaLoad(item.id)}
                    onError={(e) => {
                      // debugService.warn("Ошибка загрузки миниатюры видео", {
                      //   videoId: item.id,
                      // });
                      if (e.currentTarget) {
                        e.currentTarget.src =
                          ThumbnailGenerator.getDefaultThumbnail();
                      }
                      // Для видео отмечаем ошибку загрузки миниатюры, но не скрываем элемент
                      handleMediaError(item.id, true);
                    }}
                  />
                  {/* Индикатор видео только для миниатюр */}
                  <div className={styles.videoIndicator}>▶</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Не рендерим ничего, если нет медиа или все еще загружаются
  if (
    mediaItems.filter(
      (item) => (!item.hasError || item.type === "video") && !item.isLoading
    ).length === 0
  ) {
    return null;
  }

  return (
    <div className={styles.mediaGalleryContainer}>
      {renderThumbnails()}

      {isModalOpen &&
        selectedIndex >= 0 &&
        selectedIndex <
          mediaItems.filter(
            (item) =>
              (!item.hasError || item.type === "video") && !item.isLoading
          ).length && (
          <MediaModal
            mediaItem={
              mediaItems.filter(
                (item) =>
                  (!item.hasError || item.type === "video") && !item.isLoading
              )[selectedIndex]
            }
            onClose={handleCloseModal}
            onNext={
              selectedIndex <
              mediaItems.filter(
                (item) =>
                  (!item.hasError || item.type === "video") && !item.isLoading
              ).length -
                1
                ? goToNextMedia
                : undefined
            }
            onPrev={selectedIndex > 0 ? goToPrevMedia : undefined}
            allItems={mediaItems.filter(
              (item) =>
                (!item.hasError || item.type === "video") && !item.isLoading
            )}
            currentIndex={selectedIndex}
            onSelectIndex={setSelectedIndex}
          />
        )}
    </div>
  );
};

export default MediaGallery;
